import { intoStreamKey } from "../../streams/StreamUtils.js";

export default class StreamEvents {
	#streamMgr;

	constructor(streamMgr) {
		this.#streamMgr = streamMgr;
	}

	/**
	 * Pushes a new request range to the data request pipeline for stream events.
	 * @param {Array} streamKeys array of streamKeys to request raw data for
	 * @param {Array} dateRange [start, end] in milliseconds
	 * @param {string} contextId unique ID from the caller, passed back through the DT_STREAM_EVENTS_DATA_EVENT
	 */
	async requestStreamEvents(streamKeys, dateRange, contextId) {
		if (!streamKeys?.length) return;
		const model = this.#streamMgr.defaultModel;
		return model.asyncPropertyOperation({
			operation: "REQUEST_STREAM_EVENTS",
			streamKeys,
			dateRange,
			contextId,
		});
	}

	/**
	 * Get all events in cache within the dateRange.
	 * Note: Caller can first call isAllDataAvailable to determine if there are gaps in the data.
	 * @param {string[]} streamKeys array of streamKeys
	 * @param {Array} dateRange [start, end] in milliseconds
	 * @param {boolean} includeInitialEvent defaults false, set true to prepend initial state events at `start` for each stream
	 * @param {boolean} grouped true to return an object of event arrays, keyed by streamKey
	 * @returns {Array|Object} array of stream event objects, or a grouped object of arrays
	 */
	async getStreamEvents(streamKeys, dateRange, includeInitialEvent, grouped = false) {
		if (!streamKeys?.length) return grouped ? {} : [];
		const model = this.#streamMgr.defaultModel;
		return model.asyncPropertyOperation({
			operation: "GET_STREAM_EVENTS",
			streamKeys,
			dateRange,
			includeInitialEvent,
			grouped,
		});
	}

	/**
	 * Determine if the full range of event data for the given streams is cached and immediately available.
	 * @param {string[]} streamKeys array of streamKeys (use intoStreamKey)
	 * @param {Array} dateRange [start, end] in milliseconds
	 * @returns {boolean} yea or nay
	 */
	async isAllDataAvailable(streamKeys, dateRange) {
		if (!streamKeys?.length) return true;
		const model = this.#streamMgr.defaultModel;
		return model.asyncPropertyOperation({
			operation: "GET_STREAM_EVENTS_DATA_COMPLETE",
			streamKeys,
			dateRange,
		});
	}

	/**
	 * Clear all cached event data. Waits for active requests to finish.
	 */
	async clearStreamEvents() {
		const model = this.#streamMgr.defaultModel;
		return model?.asyncPropertyOperation({
			operation: "CLEAR_STREAM_EVENTS_CACHE",
		});
	}
	
	/**
	 * Mark all matching stream event cache blocks as stale
	 */
	async handleReadingsChanged(patch) {
		const model = this.#streamMgr.defaultModel;
		const streamKeys = [];
		const now = new Date().valueOf();
		const dateRange = [now, now];
		for (let i = 0; i < patch.keys.length; i++) {
			const change = patch.change.get(patch.dbIds[i]);
			const fullId = patch.keys[i];
			for (const [attrId, val] of Object.entries(change)) {
				streamKeys.push(intoStreamKey(fullId, attrId));
				const ts = Object.keys(val)[0] * 1000;
				if (ts < dateRange[0]) dateRange[0] = ts;
				if (ts > dateRange[1]) dateRange[1] = ts;
			}
		}
		return model.asyncPropertyOperation({
			operation: "STREAM_EVENTS_MARK_STALE",
			streamKeys,
			dateRange,
		});
	}
}
